import { FAKE, REAL, registerDependency } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import {
  GetPlannedDeliveryDates,
  PlannedDeliveryDateDTO,
  PlannedDeliveryDateItemDTO
} from '@cloudsuite/cs-ordering-defs'
import { executeOrderingRequest } from 'components/ordering/executor.js'

export const PLANNED_DELIVERY_DATES_FETCHER = Symbol('PLANNED_DELIVERY_DATES_FETCHER')

export interface PlannedDeliveryDatesFetcher {
  getPlannedDeliveryDates(
    shopId: number,
    partyId: number,
    userId: number,
    historicalDays: number,
  ): Promise<PlannedDeliveryDate[]>
}

export type PlannedDeliveryItem = {
  productId: number
  saleId: number
  saleReference: string | null
  erpOrderNumber: string | null
  saleDate: Date
  saleToken: string
  quantity: number
}

export type PlannedDeliveryDate = {
  date: Date
  items: PlannedDeliveryItem[]
}

@registerDependency(FAKE, PLANNED_DELIVERY_DATES_FETCHER)
export class FakePlannedDeliveryDatesFetcher implements PlannedDeliveryDatesFetcher {
  public static readonly EXPECTED_SHOP_ID = 1995
  public static readonly EXPECTED_PARTY_ID = 1998
  public static readonly EXPECTED_USER_ID = 1996
  public static readonly EXPECTED_HISTORICAL_DAYS = 3

  planned_delivery_dates: PlannedDeliveryDate[] = []

  insertPlannedDeliveryDate(plannedDeliveryDate: PlannedDeliveryDate) {
    this.planned_delivery_dates.push(
      plannedDeliveryDate
    )
  }

  async getPlannedDeliveryDates(
    shopId: number,
    partyId: number,
    userId: number,
    historicalDays: number,
  ): Promise<PlannedDeliveryDate[]> {
    if (shopId !== FakePlannedDeliveryDatesFetcher.EXPECTED_SHOP_ID) {
      throw Error(`wrong shop id: got ${shopId} of type ${typeof shopId}, expected ${FakePlannedDeliveryDatesFetcher.EXPECTED_SHOP_ID}`)
    }
    if (partyId !== FakePlannedDeliveryDatesFetcher.EXPECTED_PARTY_ID) {
      throw Error(`wrong party id: got ${partyId} of type ${typeof partyId}, expected ${FakePlannedDeliveryDatesFetcher.EXPECTED_PARTY_ID}`)
    }
    if (userId !== FakePlannedDeliveryDatesFetcher.EXPECTED_USER_ID) {
      throw Error(`wrong user id: got ${userId} of type ${typeof userId}, expected ${FakePlannedDeliveryDatesFetcher.EXPECTED_USER_ID}`)
    }
    if (historicalDays !== FakePlannedDeliveryDatesFetcher.EXPECTED_HISTORICAL_DAYS) {
      throw Error(`wrong historicalDays: got ${historicalDays} of type ${typeof historicalDays}, expected ${FakePlannedDeliveryDatesFetcher.EXPECTED_HISTORICAL_DAYS}`)
    }
    return this.planned_delivery_dates
  }
}

@registerDependency(REAL, PLANNED_DELIVERY_DATES_FETCHER)
export class RealPlannedDeliveryDatesFetcher implements PlannedDeliveryDatesFetcher {
  async getPlannedDeliveryDates(
    shopId: number,
    partyId: number,
    userId: number,
    historicalDays: number,
  ): Promise<PlannedDeliveryDate[]> {
    const response = await executeOrderingRequest(new GetPlannedDeliveryDates(shopId, partyId, userId, historicalDays))
    return response.planned_dates.map(deserializeDate)
  }
}

export const deserializeItem = (input: PlannedDeliveryDateItemDTO): PlannedDeliveryItem => {
  return {
    productId: input.product_id,
    saleId: input.sale_id,
    saleReference: input.sale_reference,
    erpOrderNumber: input.erp_order_number,
    saleDate: new Date(input.sale_date),
    saleToken: input.sale_token,
    quantity: +input.quantity,
  }
}

export const deserializeDate = (input: PlannedDeliveryDateDTO): PlannedDeliveryDate => {
  return {
    date: input.planned_date,
    items: input.items.map(item => deserializeItem(item))
  }
}
